import { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface SupportChatProps {
  provider: any;
  onUnreadChange: (count: number) => void;
}

export default function SupportChat({ provider, onUnreadChange }: SupportChatProps) {
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (provider) {
      loadMessages();
      
      // Subscribe to new messages
      const channel = supabase
        .channel('support_chat')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'admin_messages',
            filter: `conversation_id=eq.${provider.id}`
          },
          (payload) => {
            setMessages(prev => [...prev, payload.new]);
            scrollToBottom();
            
            // Show typing indicator briefly
            if (payload.new.sender_type === 'admin') {
              setIsTyping(true);
              setTimeout(() => setIsTyping(false), 1000);
            }
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [provider]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    if (!provider) return;

    const { data } = await supabase
      .from('admin_messages')
      .select('*')
      .eq('conversation_id', provider.id)
      .order('created_at', { ascending: true });

    if (data) {
      setMessages(data);
      
      // Mark messages as read
      await supabase
        .from('admin_messages')
        .update({ is_read: true })
        .eq('conversation_id', provider.id)
        .eq('recipient_type', 'provider')
        .eq('is_read', false);

      onUnreadChange(0);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !provider) return;

    setLoading(true);

    const { data: { user } } = await supabase.auth.getUser();
    
    const { error } = await supabase
      .from('admin_messages')
      .insert([{
        conversation_id: provider.id,
        sender_id: user?.id,
        sender_type: 'provider',
        recipient_type: 'admin',
        message: newMessage
      }]);

    if (!error) {
      setNewMessage('');
    }

    setLoading(false);
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' + 
             date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Live Support</h2>
        <p className="text-gray-600 mt-1">Chat with our admin team for help and support</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden" style={{ height: 'calc(100vh - 250px)' }}>
        <div className="flex flex-col h-full">
          {/* Chat Header */}
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-teal-100">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center text-white">
                <i className="ri-customer-service-2-fill text-2xl w-6 h-6 flex items-center justify-center"></i>
              </div>
              <div>
                <p className="font-semibold text-gray-900">Taskbudy Support Team</p>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  <p className="text-sm text-gray-600">Online - We're here to help!</p>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                  <i className="ri-chat-smile-3-line text-4xl text-teal-600 w-10 h-10 flex items-center justify-center"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Start a Conversation</h3>
                <p className="text-gray-600 max-w-md">
                  Have a question or need help? Send us a message and our support team will respond as soon as possible.
                </p>
              </div>
            ) : (
              <>
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender_type === 'provider' ? 'justify-end' : 'justify-start'} animate-[fadeIn_0.3s_ease-out]`}
                  >
                    <div className={`flex items-end space-x-2 max-w-md ${message.sender_type === 'provider' ? 'flex-row-reverse space-x-reverse' : ''}`}>
                      {message.sender_type === 'admin' && (
                        <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white flex-shrink-0">
                          <i className="ri-customer-service-2-line text-sm w-4 h-4 flex items-center justify-center"></i>
                        </div>
                      )}
                      <div>
                        <div
                          className={`px-4 py-2 rounded-2xl ${
                            message.sender_type === 'provider'
                              ? 'bg-teal-600 text-white rounded-br-none'
                              : 'bg-white text-gray-900 border border-gray-200 rounded-bl-none'
                          }`}
                        >
                          <p className="text-sm">{message.message}</p>
                        </div>
                        <p className={`text-xs mt-1 ${
                          message.sender_type === 'provider' ? 'text-right text-gray-500' : 'text-left text-gray-500'
                        }`}>
                          {formatTime(message.created_at)}
                        </p>
                      </div>
                      {message.sender_type === 'provider' && (
                        <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center text-gray-700 flex-shrink-0 font-semibold text-sm">
                          {provider?.full_name?.[0]?.toUpperCase() || 'P'}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start animate-[fadeIn_0.3s_ease-out]">
                    <div className="flex items-end space-x-2">
                      <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white">
                        <i className="ri-customer-service-2-line text-sm w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <div className="px-4 py-3 rounded-2xl bg-white border border-gray-200 rounded-bl-none">
                        <div className="flex space-x-1">
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                          <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Message Input */}
          <form onSubmit={sendMessage} className="p-4 border-t border-gray-200 bg-white">
            <div className="flex space-x-3">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
              />
              <button
                type="submit"
                disabled={loading || !newMessage.trim()}
                className="w-12 h-12 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all duration-300 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transform hover:scale-105"
              >
                <i className="ri-send-plane-fill text-xl w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Quick Help Section */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200 hover:border-teal-500 transition-colors cursor-pointer">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-question-line text-xl text-teal-600 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-1">FAQs</h4>
              <p className="text-xs text-gray-600">Find answers to common questions</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200 hover:border-teal-500 transition-colors cursor-pointer">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-book-open-line text-xl text-teal-600 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-1">Help Center</h4>
              <p className="text-xs text-gray-600">Browse our documentation</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200 hover:border-teal-500 transition-colors cursor-pointer">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-phone-line text-xl text-teal-600 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-1">Call Us</h4>
              <p className="text-xs text-gray-600">+44 20 1234 5678</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
